import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Facebook, Instagram, Check, } from 'lucide-react';
import kitchen1 from './images/kitchen1.jpeg';
import bathroom1 from './images/bathroom0.jpeg';
import tv1 from './images/tv1.jpeg';
import KITCHEN2 from './images/KITCHEN2.jpeg';
import KITCHEN3 from './images/KITCHEN3.jpeg';

// Types
interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface ProcessStep {
  number: number;
  title: string;
  description: string;
}

interface PortfolioItem {
  id: string;
  category: string;
  image: string;
  title: string;
  type: 'image' | 'video';
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  rating: number;
}

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    category: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // Data
  const services: Service[] = [
    {
      id: '1',
      title: 'Custom Kitchens',
      description: 'Tailored kitchen designs that blend functionality with elegance, crafted to your exact specifications.',
      icon: '🏠'
    },
    {
      id: '2',
      title: 'Dressing Rooms',
      description: 'Sophisticated wardrobe solutions with premium finishes and intelligent storage systems.',
      icon: '👔'
    },
    {
      id: '3',
      title: 'TV Units',
      description: 'Contemporary entertainment centers that seamlessly integrate with your living space.',
      icon: '📺'
    },
    {
      id: '4',
      title: 'Toilet Units',
      description: 'Luxurious bathroom cabinetry designed for both beauty and practicality.',
      icon: '🚿'
    },
    {
      id: '5',
      title: 'Cladding',
      description: 'Premium wall cladding solutions that add texture and sophistication to any room.',
      icon: '🎨'
    },
    {
      id: '6',
      title: '2D & 3D Design',
      description: 'Detailed visualization services to bring your vision to life before implementation.',
      icon: '📐'
    }
  ];

  const processSteps: ProcessStep[] = [
    {
      number: 1,
      title: 'Send Dimensions',
      description: 'Share your space measurements and requirements with our team'
    },
    {
      number: 2,
      title: 'Estimated Cost',
      description: 'Receive a preliminary budget estimate for your project'
    },
    {
      number: 3,
      title: '2D/3D Design',
      description: 'View detailed visualizations of your custom design'
    },
    {
      number: 4,
      title: 'Final Quotation',
      description: 'Review and approve the comprehensive project proposal'
    },
    {
      number: 5,
      title: 'Implementation',
      description: 'Expert craftsmen bring your design to life'
    },
    {
      number: 6,
      title: 'Delivery',
      description: 'Complete installation within 40 days'
    }
  ];

  const portfolioItems: PortfolioItem[] = [
    {
      id: '1',
      category: 'kitchens',
      image: kitchen1,
      title: 'Modern Kitchen',
      type: 'image'
    },
    {
      id: '2',
      category: 'dressing',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop',
      title: 'Luxury Walk-in Closet',
      type: 'image'
    },
    {
      id: '3',
      category: 'kitchens',
      image: KITCHEN3,
      title: 'Contemporary Kitchen Design',
      type: 'image'
    },
    {
      id: '4',
      category: 'tv',
      image: tv1,
      title: 'Entertainment Center',
      type: 'image'
    },
    {
      id: '5',
      category: 'bathrooms',
      image: bathroom1,
      title: 'Bathroom',
      type: 'image'
    },
    {
      id: '6',
      category: 'kitchens',
      image: KITCHEN2,
      title: 'Minimalist Kitchen',
      type: 'image'
    }
  ];

  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Madame Rima',
      role: 'Homeowner',
      content: 'I live in Germany, and honestly, the finishing of their work is outstanding. The quality is so impressive and I suggest they should bring their craftsmanship to Germany. Truly exceptional!',
      rating: 5
    },
    {
      id: '2',
      name: 'Hala',
      role: 'Homeowner',
      content: 'They delivered my kitchen earlier than the expected date, and the quality is flawless—perfect from every angle. I couldn’t be happier with the final result.',
      rating: 5
    },
    {
      id: '3',
      name: 'Reham',
      role: 'Interior Designer',
      content: 'You exceeded my expectations. The work turned out even better than I imagined. Thank you for such impressive attention to detail.',
      rating: 5
    }
  ];

  // Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    // REPLACE THIS URL with your Google Apps Script Web App URL
    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyjZ1mVOzQ-WXqizFINJczQduKd-H-Zx3Zh7ajrwyM6CrtUUpSpgVPpM-j9S3t4ePEx/exec';

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors', // Important for Google Apps Script
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      // Note: With no-cors mode, we can't read the response
      // But if no error is thrown, the request succeeded
      setFormStatus('success');
      setFormData({ name: '', phone: '', email: '', category: '', message: '' });

      setTimeout(() => setFormStatus('idle'), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const filteredPortfolio = activeFilter === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeFilter);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="font-sans bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="text-2xl font-bold" style={{ color: '#D4AF37' }}>
              EMANOUR
            </div>

            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('hero')} className="text-gray-300 hover:text-yellow-500 transition font-medium">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-yellow-500 transition font-medium">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-yellow-500 transition font-medium">Services</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-gray-300 hover:text-yellow-500 transition font-medium">Portfolio</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-yellow-500 transition font-medium">Contact</button>
            </div>

            <button onClick={() => scrollToSection('contact')} className="hidden md:block bg-yellow-600 text-gray-900 px-6 py-2 rounded hover:bg-yellow-500 transition font-semibold">
              Visit Our Showroom
            </button>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-300">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-gray-900 border-t border-gray-800">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('hero')} className="block w-full text-left text-gray-300 hover:text-yellow-500 py-2">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-300 hover:text-yellow-500 py-2">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-300 hover:text-yellow-500 py-2">Services</button>
              <button onClick={() => scrollToSection('portfolio')} className="block w-full text-left text-gray-300 hover:text-yellow-500 py-2">Portfolio</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-300 hover:text-yellow-500 py-2">Contact</button>
              <button onClick={() => scrollToSection('contact')} className="w-full bg-yellow-600 text-gray-900 px-6 py-2 rounded hover:bg-yellow-500 font-semibold">Get Started</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60 z-10"></div>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1556911220-bff31c812dba?w=1920&h=1080&fit=crop)',
            backgroundAttachment: 'fixed'
          }}
        ></div>

        <div className="relative z-20 text-center text-white px-4 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in" style={{ color: '#D4AF37' }}>
            Premium Custom Kitchens<br />& Dressing Rooms
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300">
            High-quality materials • Professional design • 5-year warranty
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => scrollToSection('contact')} className="bg-yellow-600 text-gray-900 px-8 py-4 rounded-lg hover:bg-yellow-500 transition text-lg font-semibold">
              Get Your Free Design Consultation
            </button>
            <button onClick={() => scrollToSection('portfolio')} className="bg-white/10 backdrop-blur text-white border-2 border-yellow-600 px-8 py-4 rounded-lg hover:bg-white/20 transition text-lg font-semibold">
              View Portfolio
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <ChevronDown className="text-yellow-500 animate-bounce" size={32} />
        </div>
      </section>

      {/* About / Why Choose Us */}
      <section id="about" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>Why Choose EMANOUR</h2>
            <p className="text-xl text-gray-400">Excellence in every detail</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-800 p-8 rounded-lg shadow-lg text-center hover:shadow-2xl hover:bg-gray-750 transition border border-gray-700">
              <div className="text-5xl mb-4">💎</div>
              <h3 className="text-xl font-bold mb-3 text-yellow-500">Quality Materials</h3>
              <p className="text-gray-400">Premium materials sourced from trusted suppliers for lasting beauty</p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg shadow-lg text-center hover:shadow-2xl hover:bg-gray-750 transition border border-gray-700">
              <div className="text-5xl mb-4">🎨</div>
              <h3 className="text-xl font-bold mb-3 text-yellow-500">Custom Design</h3>
              <p className="text-gray-400">Professional designers create spaces tailored to your lifestyle</p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg shadow-lg text-center hover:shadow-2xl hover:bg-gray-750 transition border border-gray-700">
              <div className="text-5xl mb-4">🛡️</div>
              <h3 className="text-xl font-bold mb-3 text-yellow-500">5-Year Warranty</h3>
              <p className="text-gray-400">Complete peace of mind with our comprehensive warranty coverage</p>
            </div>

            <div className="bg-gray-800 p-8 rounded-lg shadow-lg text-center hover:shadow-2xl hover:bg-gray-750 transition border border-gray-700">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-xl font-bold mb-3 text-yellow-500">40-Day Delivery</h3>
              <p className="text-gray-400">Efficient process from design to installation in just 40 days</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>Our Services</h2>
            <p className="text-xl text-gray-400">Comprehensive interior solutions</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div key={service.id} className="group bg-gray-900 p-8 rounded-lg hover:bg-gray-800 transition duration-300 border border-gray-800">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-2xl font-bold mb-3 group-hover:text-yellow-500 transition text-white">{service.title}</h3>
                <p className="text-gray-400 mb-4">{service.description}</p>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-gray-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>Our Process</h2>
            <p className="text-xl text-gray-400">From concept to completion in 6 simple steps</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.number} className="relative">
                <div className="bg-gray-900 p-8 rounded-lg hover:bg-gray-800 transition border border-gray-800">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center font-bold text-xl text-gray-900">
                      {step.number}
                    </div>
                    {index < processSteps.length - 1 && (
                      <div className="hidden lg:block flex-1 h-0.5 bg-yellow-600/30 ml-4"></div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-yellow-500">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section id="portfolio" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>Our Portfolio</h2>
            <p className="text-xl text-gray-400">Explore our latest projects</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-6 py-2 rounded-full transition ${activeFilter === 'all' ? 'bg-yellow-600 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}
            >
              All Projects
            </button>
            <button
              onClick={() => setActiveFilter('kitchens')}
              className={`px-6 py-2 rounded-full transition ${activeFilter === 'kitchens' ? 'bg-yellow-600 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}
            >
              Kitchens
            </button>
            <button
              onClick={() => setActiveFilter('dressing')}
              className={`px-6 py-2 rounded-full transition ${activeFilter === 'dressing' ? 'bg-yellow-600 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}
            >
              Dressing Rooms
            </button>
            <button
              onClick={() => setActiveFilter('tv')}
              className={`px-6 py-2 rounded-full transition ${activeFilter === 'tv' ? 'bg-yellow-600 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}
            >
              TV Units
            </button>
            <button
              onClick={() => setActiveFilter('bathrooms')}
              className={`px-6 py-2 rounded-full transition ${activeFilter === 'bathrooms' ? 'bg-yellow-600 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}
            >
              Bathrooms
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPortfolio.map((item) => (
              <div
                key={item.id}
                className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition cursor-pointer border border-gray-800"
                onClick={() => setLightboxImage(item.image)}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-80 object-cover group-hover:scale-110 transition duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent flex items-end p-6">
                  <h3 className="text-white text-xl font-bold">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>Client Testimonials</h2>
            <p className="text-xl text-gray-400">What our clients say about us</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gray-800 p-8 rounded-lg shadow-lg border border-gray-700">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="font-bold text-white">{testimonial.name}</p>
                  <p className="text-gray-400 text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>Get In Touch</h2>
            <p className="text-xl text-gray-400">Start your dream project today</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-gray-900 p-8 rounded-lg border border-gray-800">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded bg-gray-800 border border-gray-700 text-white focus:border-yellow-600 focus:ring-2 focus:ring-yellow-600/20 outline-none transition"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded bg-gray-800 border border-gray-700 text-white focus:border-yellow-600 focus:ring-2 focus:ring-yellow-600/20 outline-none transition"
                    placeholder="+20 100 000 0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded bg-gray-800 border border-gray-700 text-white focus:border-yellow-600 focus:ring-2 focus:ring-yellow-600/20 outline-none transition"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 rounded bg-gray-800 border border-gray-700 text-white focus:border-yellow-600 focus:ring-2 focus:ring-yellow-600/20 outline-none transition"
                  >
                    <option value="">Select your category</option>
                    <option value="homeowner">Homeowner</option>
                    <option value="designer">Interior Designer</option>
                    <option value="contractor">Contractor</option>
                    <option value="highend">High-end Client</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Message *</label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 rounded bg-gray-800 border border-gray-700 text-white focus:border-yellow-600 focus:ring-2 focus:ring-yellow-600/20 outline-none transition resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full bg-yellow-600 text-gray-900 py-4 rounded-lg hover:bg-yellow-500 transition font-semibold text-lg disabled:opacity-50"
                >
                  {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green-900/50 text-green-300 p-4 rounded-lg flex items-center gap-2 border border-green-700">
                    <Check size={20} />
                    Thank you! We'll contact you soon.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-900/50 text-red-300 p-4 rounded-lg border border-red-700">
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-6 text-yellow-500">Contact Information</h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="bg-yellow-600/20 p-3 rounded-lg border border-yellow-600/30">
                      <Phone className="text-yellow-500" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Phone</p>
                      <p className="text-gray-400">01090074984</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-green-600/20 p-3 rounded-lg border border-green-600/30">
                      <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-white">WhatsApp</p>
                      <p className="text-gray-400">01016996990 - </p>
                      <a href="https://wa.me/01016996990" target="_blank" rel="noopener noreferrer" className="text-yellow-500 hover:text-yellow-400 text-sm">
                        Chat with us →
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-blue-600/20 p-3 rounded-lg border border-blue-600/30">
                      <Mail className="text-blue-400" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Email</p>
                      <p className="text-gray-400">emanour.eg@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-red-600/20 p-3 rounded-lg border border-red-600/30">
                      <MapPin className="text-red-400" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Location</p>
                      <p className="text-gray-400">Al Shorouk - Al Horreya Street, Behind Dar Misr</p>
                      <p className="text-gray-400 text-sm">Next To The New Carrefour Inside Wataniya Gas Station</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-600/10 p-6 rounded-lg border border-yellow-600/30">
                <h4 className="font-bold text-lg mb-3 text-yellow-500">Business Hours</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Saturday - Thursday:</span>
                    <span className="font-semibold text-white">12:00 PM - 10:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Friday:</span>
                    <span className="font-semibold text-white">Closed</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-lg mb-4 text-yellow-500">Follow Us</h4>
                <div className="flex gap-4">
                  <a href="https://www.facebook.com/emanour.eg" className="bg-gray-800 p-3 rounded-full hover:bg-yellow-600 hover:text-gray-900 transition border border-gray-700">
                    <Facebook size={24} />
                  </a>
                  <a href="https://www.instagram.com/emanour.eg/" className="bg-gray-800 p-3 rounded-full hover:bg-yellow-600 hover:text-gray-900 transition border border-gray-700">
                    <Instagram size={24} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-white py-12 border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#D4AF37' }}>
                EMANOUR
              </h3>
              <p className="text-gray-400">
                Crafting beautiful, functional spaces with premium materials and expert craftsmanship.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-yellow-500">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-yellow-500 transition">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-yellow-500 transition">Services</button></li>
                <li><button onClick={() => scrollToSection('portfolio')} className="hover:text-yellow-500 transition">Portfolio</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-yellow-500 transition">Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-yellow-500">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="hover:text-yellow-500 transition cursor-pointer">Custom Kitchens</li>
                <li className="hover:text-yellow-500 transition cursor-pointer">Dressing Rooms</li>
                <li className="hover:text-yellow-500 transition cursor-pointer">TV Units</li>
                <li className="hover:text-yellow-500 transition cursor-pointer">Bathroom Units</li>
                <li className="hover:text-yellow-500 transition cursor-pointer">Wall Cladding</li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-yellow-500">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>01090074984</li>
                <li>01016996990</li>
                <li>info@emanour.com</li>
                <li className="text-sm">Al Shorouk, Cairo, Egypt</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-900 pt-8 text-center text-gray-500">
            <p>&copy; {new Date().getFullYear()} EMANOUR. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-bounce {
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        html {
          scroll-behavior: smooth;
        }

        * {
          box-sizing: border-box;
        }

        .bg-gray-750 {
          background-color: #2d3748;
        }
      `}</style>
      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white hover:text-yellow-500 transition"
          >
            <X size={40} />
          </button>
          <img
            src={lightboxImage}
            alt="Full view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default App;